<div class="order-upload-page w-100">
	<div class="d-flex justify-content-between align-items-center flex-wrap order-upload-toolbar mb-2">
		<div class="mb-0 text-muted small order-upload-toolbar__meta">
			<ng-container *ngIf="files.length && !orderReviewReady">
				<span class="font-weight-bold text-dark">{{ files.length }} file(s) queued</span>
				<span class="text-muted"> · Process files to map columns</span>
			</ng-container>
			<ng-container *ngIf="orderReviewReady && uploadedRawData?.length">
				<span class="font-weight-bold text-dark">{{ uploadedRawData.length }} row(s)</span>
				<span class="text-muted"> · {{ availableFiles.length }} file(s)</span>
			</ng-container>
		</div>
		<button
			mat-stroked-button
			type="button"
			class="mt-1"
			(click)="$event.preventDefault(); toggleUploadHelp()"
			(keydown.enter)="$event.preventDefault()">
			Help
		</button>
	</div>

	<div class="modal-body pomodalbody w-100 order-upload-body px-0">
		<ng-container *ngIf="!poHelp">
			<div *ngIf="!processing_recs && !orderReviewReady">
				<mat-card class="order-upload-panel mb-3">
					<mat-card-subtitle class="order-upload-card-heading">Upload order lines</mat-card-subtitle>
					<mat-card-content>
						<p class="text-muted small mb-3">
							<strong>CSV or Excel</strong> — first sheet only. Files are read in the browser. Map columns, then
							<strong>Apply Mappings</strong> to validate SKUs; nothing is added to the cart until you confirm.
						</p>
						<ngx-dropzone
							class="inv-upload-dropzone"
							[ngClass]="'inv-upload-dropzone--' + dropzoneThemeColor"
							[multiple]="true"
							[expandable]="true"
							accept=".csv,.xlsx,.xls,text/csv,text/plain,application/csv,text/x-csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
							(change)="onSelect($event)">
							<ngx-dropzone-label>
								<div class="order-upload-dropzone__inner">
									<mat-icon class="order-upload-dropzone__icon" aria-hidden="true">cloud_upload</mat-icon>
									<span class="order-upload-dropzone__title">Drag and drop order files here</span>
									<span class="order-upload-dropzone__hint"
										>or click to browse — you can add multiple files; they merge into one mapping step.</span
									>
								</div>
							</ngx-dropzone-label>
							<ngx-dropzone-preview *ngFor="let f of files" [file]="f" [removable]="true" (removed)="onRemove(f)">
								<ngx-dropzone-label class="order-upload-file-label">{{ f.name }} · {{ formatFileSizeKb(f.size) }} KB</ngx-dropzone-label>
							</ngx-dropzone-preview>
						</ngx-dropzone>

						<div class="order-upload-batch-format mt-3">
							<p class="order-upload-batch-format__intro text-muted small mb-2">
								First row must be headers. Required targets (your column names can differ — use the row of selects after
								processing):
							</p>
							<div class="order-upload-batch-format__chips">
								<span class="order-upload-chip order-upload-chip--required"
									>stockid <span class="order-upload-chip__note">SKU / part #</span></span
								>
								<span class="order-upload-chip order-upload-chip--required"
									>qty_purchase <span class="order-upload-chip__note">quantity</span></span
								>
								<span class="order-upload-chip order-upload-chip--required"
									>price <span class="order-upload-chip__note">unit price, no commas</span></span
								>
								<span class="order-upload-chip order-upload-chip--optional"
									>description <span class="order-upload-chip__note">optional</span></span
								>
							</div>
							<button mat-stroked-button type="button" class="mt-2" (click)="downloadOrderUploadTemplate()" [disabled]="processing_recs">
								<mat-icon>file_download</mat-icon>
								Download template
							</button>
						</div>

						<div class="d-flex flex-wrap justify-content-between align-items-center mt-3 pt-2 border-top">
							<button mat-stroked-button color="warn" type="button" (click)="clearAllFiles()" [disabled]="processing_recs || !files.length">
								<mat-icon>delete_sweep</mat-icon>
								Clear all
							</button>
						</div>
					</mat-card-content>
				</mat-card>
			</div>

			<div class="order-upload-processing text-center py-4" *ngIf="processing_recs">
				<mat-spinner diameter="48" class="order-upload-processing__spinner"></mat-spinner>
				<p class="order-upload-processing__title mt-3 mb-1">Processing files…</p>
				<p class="order-upload-processing__hint text-muted small mb-0">Reading spreadsheets in your browser and preparing column mapping.</p>
			</div>

			<mat-card class="order-upload-panel" *ngIf="orderReviewReady">
				<mat-card-subtitle class="order-upload-card-heading">Review &amp; map</mat-card-subtitle>
				<mat-card-content class="p-0 pt-2">
					<mat-tab-group *ngIf="uploadedRawData && uploadedRawData.length > 0" [(selectedIndex)]="orderUploadSelectedTabIndex">
						<mat-tab>
							<ng-template mat-tab-label>
								<span>Mapping&nbsp;<mat-icon class="align-middle ou-tab-icon">table_chart</mat-icon></span>
							</ng-template>

							<div class="mt-3 px-1">
								<div class="alert alert-info d-flex align-items-start py-2 mb-3 small">
									<mat-icon class="flex-shrink-0 mr-2">info</mat-icon>
									<div class="mb-0">
										Each file column maps to one order field. Use the <strong>Material selects</strong> under your headers.
										When <strong>All Files</strong> is selected, the same header name shares one mapping across files.
									</div>
								</div>

								<mat-form-field appearance="outline" class="w-100 mb-3 order-upload-file-select">
									<mat-label>File view</mat-label>
									<mat-select [(value)]="selectedFile" (selectionChange)="initializeColumnMappings()">
										<mat-option value="all">All files</mat-option>
										<mat-option *ngFor="let file of availableFiles" [value]="file">
											{{ file }}
										</mat-option>
									</mat-select>
								</mat-form-field>

								<div *ngIf="selectedFile !== 'all'" class="alert alert-secondary small py-2 mb-3">
									Mappings below apply to <strong>{{ selectedFile }}</strong> only (other files keep their own column map).
								</div>

								<div *ngIf="selectedFile === 'all'" class="alert alert-secondary small py-2 mb-3">
									Combined view: one mapping per header name across all files that use that column.
								</div>

								<div *ngIf="orderUploadMappingPreviewTotal > orderUploadMappingPreviewMax" class="alert alert-secondary small py-2 mb-2">
									Showing the first {{ orderUploadMappingPreviewMax }} of {{ orderUploadMappingPreviewTotal }} row(s) in this view.
									<strong>Apply Mappings</strong> uses every row — validation runs on the server.
								</div>

								<div class="table-responsive order-upload-mapping-wrap">
									<table class="table table-bordered table-sm order-upload-mapping-table mb-0">
										<thead>
											<tr>
												<ng-container *ngIf="selectedFile === 'all'">
													<th *ngFor="let header of uploadedHeaders" class="small text-muted order-upload-th-title" [attr.title]="header">
														<span class="order-upload-th-text">{{ header }}</span>
													</th>
												</ng-container>
												<ng-container *ngIf="selectedFile !== 'all'">
													<th *ngFor="let header of fileSpecificHeaders[selectedFile]" class="small text-muted order-upload-th-title" [attr.title]="header">
														<span class="order-upload-th-text">{{ header }}</span>
													</th>
												</ng-container>
											</tr>
											<tr>
												<ng-container *ngIf="selectedFile === 'all'">
													<th *ngFor="let header of uploadedHeaders" class="p-1 align-top" [ngClass]="{ 'border-danger': dupedSuppliedHeaders[header] }">
														<mat-form-field appearance="outline" class="w-100 order-upload-map-field">
															<mat-select [value]="columnMappings[header]" (selectionChange)="updateColumnMapping(header, $event.value)">
																<mat-option *ngFor="let type of availableFieldTypes" [value]="type">
																	<span [ngClass]="suppliedHeaders[type] ? 'text-danger' : ''">{{ orderUploadFieldLabel(type) }}</span>
																</mat-option>
															</mat-select>
														</mat-form-field>
													</th>
												</ng-container>

												<ng-container *ngIf="selectedFile !== 'all'">
													<th *ngFor="let header of fileSpecificHeaders[selectedFile]" class="p-1 align-top">
														<mat-form-field appearance="outline" class="w-100 order-upload-map-field">
															<mat-select
																[value]="fileSpecificMappings[selectedFile][header]"
																(selectionChange)="updateColumnMapping(header, $event.value, selectedFile)">
																<mat-option *ngFor="let type of availableFieldTypes" [value]="type">
																	<span [ngClass]="suppliedHeaders[type] ? 'text-danger' : ''">{{ orderUploadFieldLabel(type) }}</span>
																</mat-option>
															</mat-select>
														</mat-form-field>
													</th>
												</ng-container>
											</tr>
										</thead>
										<tbody>
											<ng-container *ngIf="selectedFile === 'all'">
												<tr *ngFor="let row of orderUploadMappingPreviewRows">
													<td *ngFor="let header of uploadedHeaders" class="small">{{ row[header] }}</td>
												</tr>
											</ng-container>

											<ng-container *ngIf="selectedFile !== 'all'">
												<tr *ngFor="let row of orderUploadMappingPreviewRows">
													<td *ngFor="let header of fileSpecificHeaders[selectedFile]" class="small">{{ row[header] }}</td>
												</tr>
											</ng-container>
										</tbody>
									</table>
								</div>
							</div>
						</mat-tab>
						<mat-tab *ngIf="orderDataSource && orderDataSource['good']">
							<ng-template mat-tab-label>
								<span
									>Recognized&nbsp;({{ orderDataSource['good'].data.length }})&nbsp;<mat-icon class="text-success align-middle">done</mat-icon></span
								>
							</ng-template>
							<div class="table-responsive mt-3">
								<table class="table table-striped table-sm order-upload-result-table mb-0">
									<thead>
										<tr>
											<th>Stock ID</th>
											<th>Description</th>
											<th>Quantity</th>
											<th>Price</th>
										</tr>
									</thead>
									<tbody>
										<tr *ngFor="let element of orderDataSource['good'].data">
											<td>{{ element.stockid }}</td>
											<td>{{ element.description }}</td>
											<td class="text-center">{{ element.quantity }}</td>
											<td>{{ element.price | currency }}</td>
										</tr>
									</tbody>
								</table>
							</div>
						</mat-tab>
						<mat-tab *ngIf="orderDataSource && orderDataSource['bad']">
							<ng-template mat-tab-label>
								<span
									>Unrecognized&nbsp;({{ orderDataSource['bad'].data.length }})&nbsp;<mat-icon class="text-danger align-middle">announcement</mat-icon></span
								>
							</ng-template>
							<div class="w-100 mt-1 mb-1 justify-content-end">
								<button mat-raised-button class="btn-danger m-1" (click)="exportBad('pdf')">PDF</button>
								<button mat-raised-button class="btn-success m-1" (click)="exportBad('xlsx')">XLS</button>
							</div>
							<div class="table-responsive mt-3">
								<table class="table table-striped table-sm order-upload-result-table mb-0">
									<thead>
										<tr>
											<th>Stock ID</th>
											<th>Description</th>
											<th>Quantity</th>
											<th>Price</th>
										</tr>
									</thead>
									<tbody>
										<tr *ngFor="let element of orderDataSource['bad'].data">
											<td>{{ element.stockid }}</td>
											<td>{{ element.description }}</td>
											<td class="text-center">{{ element.quantity }}</td>
											<td>{{ element.price | currency }}</td>
										</tr>
									</tbody>
								</table>
							</div>
						</mat-tab>
					</mat-tab-group>
				</mat-card-content>
			</mat-card>
		</ng-container>

		<ng-container *ngIf="poHelp">
			<div class="row">
				<div class="col-md-6">
					<h4 class="font-weight-bold">Headers</h4>
					<p class="text-muted small">Column names in the file can differ; map them to these targets in the grid.</p>
					<ul class="small">
						<li><strong>stockid</strong> — SKU as in inventory</li>
						<li><strong>qty_purchase</strong> — quantity (0 lines are skipped)</li>
						<li><strong>price</strong> — unit price without commas (blank may use customer default)</li>
						<li><strong>description</strong> — optional line text</li>
					</ul>
					<hr />
					<h4 class="font-weight-bold">Notes</h4>
					<ul class="small overflow-auto" style="max-height: 200px">
						<li>Each stockid is validated; recognized lines appear in the first results tab after Apply.</li>
						<li>Only recognized items are added when you use <strong>Add To Order</strong>.</li>
						<li>Multiple files merge into one mapping pass.</li>
					</ul>
				</div>
				<div class="col-md-6 border-md-left">
					<h4 class="font-weight-bold">Row data tips</h4>
					<ul class="small">
						<li>Avoid wrapping stock IDs in extra quotes.</li>
						<li>Use numeric quantities and prices (e.g. <code>78.92</code> not <code>1,274.22</code>).</li>
					</ul>
					<hr />
					<h4 class="font-weight-bold">Formats</h4>
					<ul class="small mb-0">
						<li>Excel (.xlsx, .xls)</li>
						<li>CSV (.csv)</li>
					</ul>
				</div>
			</div>
		</ng-container>
	</div>

	<div class="modal-footer order-upload-footer d-flex flex-wrap align-items-center justify-content-end border-top pt-3">
		<div class="order-upload-footer__left mr-auto mb-2 mb-md-0 pr-2" *ngIf="orderReviewReady && !mappingsFulfilled.status">
			<small class="text-muted">Required maps:</small>
			<small class="text-danger" *ngFor="let type of requiredMappings | keyvalue">
				<ng-container *ngIf="!type.value">&nbsp;{{ orderUploadFieldLabel(type.key) }}</ng-container>
			</small>
		</div>

		<button
			mat-stroked-button
			*ngIf="!orderReviewReady"
			type="button"
			class="order-upload-footer-process"
			(click)="$event.preventDefault(); uploadOrder({ userInitiated: true })"
			(keydown.enter)="$event.preventDefault()"
			[disabled]="files.length == 0 || processing_recs">
			<mat-spinner *ngIf="processing_recs" class="order-upload-footer-process__spinner" diameter="18"></mat-spinner>
			<span *ngIf="processing_recs">Processing…</span>
			<span *ngIf="!processing_recs">Process file(s)</span>
		</button>

		<button mat-stroked-button *ngIf="orderReviewReady" type="button" (click)="$event.preventDefault(); resetUploadOrder()" (keydown.enter)="$event.preventDefault()">
			Back
		</button>

		<span class="mx-2 text-muted small align-middle" *ngIf="orderReviewReady && config?.env?.package === 'tires'">
			PLL
			<mat-slide-toggle [value]="lookupType" (toggleChange)="changeLookup()"></mat-slide-toggle>
			MFG
		</span>

		<button
			*ngIf="orderReviewReady"
			type="button"
			mat-raised-button
			color="primary"
			class="order-upload-apply-btn"
			[disabled]="!checkForRequiredFields() || hasDuplicateMappings || mappingApplyProcessing"
			(click)="applyMappingsAndProcessOrder()">
			<mat-spinner *ngIf="mappingApplyProcessing" class="order-upload-apply-btn__spinner" diameter="18"></mat-spinner>
			<mat-icon *ngIf="!mappingApplyProcessing">check</mat-icon>
			<ng-container *ngIf="mappingApplyProcessing">Applying…</ng-container>
			<ng-container *ngIf="!mappingApplyProcessing">{{ orderDataSource ? 'Re-' : '' }}Apply mappings</ng-container>
		</button>

		<button
			mat-raised-button
			color="accent"
			*ngIf="orderReviewReady && orderDataSource && orderDataSource['good']"
			type="button"
			[disabled]="hasDuplicateMappings"
			(click)="$event.preventDefault(); addOrderItems()"
			(keydown.enter)="$event.preventDefault()">
			<mat-icon>add_shopping_cart</mat-icon>
			Add to order
		</button>
	</div>
</div>
